import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { chargeKeys } from '@/hooks/use-charges';
import { enrollmentKeys } from '@/hooks/use-enrollments';
import api from '@/services/api';
import { actAsHeaders } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import {
  Appointment,
  AvailabilityBlock,
  AvailabilityInterval,
  AvailabilityResult,
  BookableProvider,
  BookableService,
  BookableServiceDetail,
  CreateAppointmentDto,
  RescheduleAppointmentDto,
  ScheduleSettings,
} from '@/types/appointment';
import { USER_ROLE } from '@/types/user-role';
import { isProviderRole } from '@/utils/user-role';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const appointmentKeys = {
  all: ['appointments'] as const,
  clientList: () => [...appointmentKeys.all, 'client'] as const,
  providers: () => [...appointmentKeys.all, 'providers'] as const,
  services: (providerId: string) => [...appointmentKeys.all, 'services', providerId] as const,
  serviceDetail: (serviceId: string) =>
    [...appointmentKeys.all, 'service-detail', serviceId] as const,
  availability: (providerId: string, serviceId: string, from: string, to: string) =>
    [...appointmentKeys.all, 'availability', providerId, serviceId, from, to] as const,
  agenda: (from: string, to: string) => [...appointmentKeys.all, 'agenda', from, to] as const,
  settings: () => [...appointmentKeys.all, 'settings'] as const,
  grid: () => [...appointmentKeys.all, 'grid'] as const,
  blocks: () => [...appointmentKeys.all, 'blocks'] as const,
};

/**
 * Todo agendamento nasce de um vínculo (e o vínculo gera cobrança), então
 * mexer na agenda invalida também contratos e cobranças — senão a tela
 * "Meus Contratos" fica servindo cache velho depois de marcar horário.
 */
function invalidateScheduleScope(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
  queryClient.invalidateQueries({ queryKey: enrollmentKeys.all });
  queryClient.invalidateQueries({ queryKey: chargeKeys.all });
}

// ---------------------------------------------------------------------------
// Cliente (Fase 2)
// ---------------------------------------------------------------------------

export function useBookableProviders() {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isClient = user?.role === USER_ROLE.CLIENT;

  return useQuery({
    queryKey: appointmentKeys.providers(),
    queryFn: async () => {
      const res = await api.get<BookableProvider[]>('/client/schedule/providers');
      return res.data;
    },
    enabled: isHydrated && isClient,
  });
}

export function useBookableServices(providerId: string | undefined) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: appointmentKeys.services(providerId ?? ''),
    queryFn: async () => {
      const res = await api.get<BookableService[]>(
        `/client/schedule/providers/${providerId}/services`,
      );
      return res.data;
    },
    enabled: isHydrated && !!providerId,
  });
}

export function useBookableServiceDetail(serviceId: string | undefined) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: appointmentKeys.serviceDetail(serviceId ?? ''),
    queryFn: async () => {
      const res = await api.get<BookableServiceDetail>(`/client/schedule/services/${serviceId}`);
      return res.data;
    },
    enabled: isHydrated && !!serviceId,
  });
}

export function useAvailability(params: {
  providerId?: string;
  serviceId?: string;
  from: string;
  to: string;
}) {
  const isHydrated = useAuthHydration();
  const { providerId, serviceId, from, to } = params;

  return useQuery({
    queryKey: appointmentKeys.availability(providerId ?? '', serviceId ?? '', from, to),
    queryFn: async () => {
      const res = await api.get<AvailabilityResult>('/client/schedule/availability', {
        params: { providerId, serviceId, from, to },
      });
      return res.data;
    },
    enabled: isHydrated && !!providerId && !!serviceId,
  });
}

/**
 * Disponibilidade vista pelo próprio prestador (token deriva o provider). Usada
 * ao montar a grade fixa de um contrato pela agenda real.
 */
export function useProviderAvailability(params: {
  serviceId?: string;
  from: string;
  to: string;
  enabled?: boolean;
}) {
  const isHydrated = useAuthHydration();
  const { serviceId, from, to, enabled = true } = params;

  return useQuery({
    queryKey: [...appointmentKeys.availability('me', serviceId ?? '', from, to)],
    queryFn: async () => {
      const res = await api.get<AvailabilityResult>('/provider/me/schedule/availability', {
        params: { serviceId, from, to },
      });
      return res.data;
    },
    enabled: isHydrated && !!serviceId && enabled,
  });
}

export function useClientAppointments() {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isClient = user?.role === USER_ROLE.CLIENT;

  return useQuery({
    queryKey: appointmentKeys.clientList(),
    queryFn: async () => {
      const res = await api.get<Appointment[]>('/client/schedule/appointments');
      return res.data;
    },
    enabled: isHydrated && isClient,
  });
}

export function useCreateAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateAppointmentDto) => {
      const res = await api.post<Appointment>('/client/schedule/appointments', data);
      return res.data;
    },
    onSuccess: () => {
      invalidateScheduleScope(queryClient);
    },
  });
}

export function useCancelAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const res = await api.post<Appointment>(`/client/schedule/appointments/${id}/cancel`, {
        reason,
      });
      return res.data;
    },
    onSuccess: () => {
      invalidateScheduleScope(queryClient);
    },
  });
}

export function useRescheduleAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: RescheduleAppointmentDto }) => {
      const res = await api.post<Appointment>(
        `/client/schedule/appointments/${id}/reschedule`,
        data,
      );
      return res.data;
    },
    onSuccess: () => {
      invalidateScheduleScope(queryClient);
    },
  });
}

// ---------------------------------------------------------------------------
// Prestador (Fase 3)
// ---------------------------------------------------------------------------

export function useScheduleSettings() {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isProvider = isProviderRole(user?.role);

  return useQuery({
    queryKey: appointmentKeys.settings(),
    queryFn: async () => {
      const res = await api.get<ScheduleSettings>('/provider/me/schedule/settings');
      return res.data;
    },
    enabled: isHydrated && isProvider,
  });
}

export function useUpdateScheduleSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<ScheduleSettings>) => {
      const res = await api.put<ScheduleSettings>('/provider/me/schedule/settings', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.settings() });
    },
  });
}

export function useAvailabilityGrid() {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isProvider = isProviderRole(user?.role);

  return useQuery({
    queryKey: appointmentKeys.grid(),
    queryFn: async () => {
      const res = await api.get<AvailabilityInterval[]>('/provider/me/schedule/grid');
      return res.data;
    },
    enabled: isHydrated && isProvider,
  });
}

export function useSetAvailabilityGrid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (intervals: AvailabilityInterval[]) => {
      const res = await api.put<AvailabilityInterval[]>('/provider/me/schedule/grid', {
        intervals,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.grid() });
    },
  });
}

export function useBlocks() {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isProvider = isProviderRole(user?.role);

  return useQuery({
    queryKey: appointmentKeys.blocks(),
    queryFn: async () => {
      const res = await api.get<AvailabilityBlock[]>('/provider/me/schedule/blocks');
      return res.data;
    },
    enabled: isHydrated && isProvider,
  });
}

export function useCreateBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      startAt: string;
      endAt: string;
      allDay?: boolean;
      reason?: string;
    }) => {
      const res = await api.post<AvailabilityBlock>('/provider/me/schedule/blocks', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.blocks() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

export function useDeleteBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/provider/me/schedule/blocks/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.blocks() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

export function useProviderAgenda(from: string, to: string) {
  const isHydrated = useAuthHydration();
  const { user } = useAuthStore();
  const isProvider = isProviderRole(user?.role);

  return useQuery({
    queryKey: appointmentKeys.agenda(from, to),
    queryFn: async () => {
      const res = await api.get<Appointment[]>('/provider/me/schedule/agenda', {
        params: { from, to },
      });
      return res.data;
    },
    enabled: isHydrated && isProvider,
  });
}

export function useProviderAppointmentAction() {
  const queryClient = useQueryClient();
  // Aprovar/rejeitar mexe no vínculo e na cobrança única, não só na agenda.
  const invalidate = () => invalidateScheduleScope(queryClient);

  return {
    approve: useMutation({
      mutationFn: async (id: string) =>
        (await api.post<Appointment>(`/provider/me/schedule/appointments/${id}/approve`)).data,
      onSuccess: invalidate,
    }),
    reject: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
        (await api.post<Appointment>(`/provider/me/schedule/appointments/${id}/reject`, { reason }))
          .data,
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
        (await api.post<Appointment>(`/provider/me/schedule/appointments/${id}/cancel`, { reason }))
          .data,
      onSuccess: invalidate,
    }),
    reschedule: useMutation({
      mutationFn: async ({ id, startAt }: { id: string; startAt: string }) =>
        (
          await api.post<Appointment>(`/provider/me/schedule/appointments/${id}/reschedule`, {
            startAt,
          })
        ).data,
      onSuccess: invalidate,
    }),
    noShow: useMutation({
      mutationFn: async (id: string) =>
        (await api.post<Appointment>(`/provider/me/schedule/appointments/${id}/no-show`)).data,
      onSuccess: invalidate,
    }),
    /** Link da chamada desta sessão online; `null` tira. */
    setMeetingUrl: useMutation({
      mutationFn: async ({ id, meetingUrl }: { id: string; meetingUrl: string | null }) =>
        (
          await api.put<Appointment>(`/provider/me/schedule/appointments/${id}/meeting-url`, {
            meetingUrl,
          })
        ).data,
      onSuccess: () => {
        invalidate();
        queryClient.invalidateQueries({ queryKey: ['provider-last-meeting-url'] });
      },
    }),
    create: useMutation({
      mutationFn: async ({
        actingProviderId,
        ...data
      }: {
        clientId: string;
        serviceId: string;
        startAt: string;
        force?: boolean;
        /** Marca na agenda deste profissional sem trocar o foco. */
        actingProviderId?: string;
      }) =>
        (
          await api.post<Appointment>(
            '/provider/me/schedule/appointments',
            data,
            actAsHeaders(actingProviderId),
          )
        ).data,
      onSuccess: invalidate,
    }),
  };
}

/** Último link de chamada que o prestador usou — só sugestão para colar de novo. */
export function useLastMeetingUrl(enabled: boolean) {
  return useQuery({
    queryKey: ['provider-last-meeting-url'],
    queryFn: async () =>
      (
        await api.get<{ meetingUrl: string | null }>(
          '/provider/me/schedule/appointments/last-meeting-url',
        )
      ).data.meetingUrl,
    enabled,
  });
}
